/**
 * rect 앵커가 사용자에게 더는 보이지 않는지 판정한다. "완전히 벗어남"만 hidden으로 본다 —
 * 경계에 걸친 앵커는 보이는 것이고, 크기 0 rect(happy-dom·측정 전)는 밖으로 나간 것이 아니다.
 */
import type { FloatingPopoverRect } from "../foundations/popover";

type Bounds = Pick<FloatingPopoverRect, "top" | "left" | "bottom" | "right">;

export type ClipAxes = { readonly x: boolean; readonly y: boolean };

/** 앵커를 잘라낼 수 있는 조상과 실제로 잘라내는 축. `overflow-x: clip; overflow-y: visible`은 x만 잘라낸다. */
export type ClipAncestor = ClipAxes & { readonly element: HTMLElement };

const BOTH_AXES: ClipAxes = { x: true, y: true };

const CLIPPING_OVERFLOW = new Set(["auto", "scroll", "hidden", "clip"]);

export function isRectOutside(rect: Bounds, bounds: Bounds, axes: ClipAxes = BOTH_AXES): boolean {
  return (
    (axes.y && (rect.bottom < bounds.top || rect.top > bounds.bottom)) ||
    (axes.x && (rect.right < bounds.left || rect.left > bounds.right))
  );
}

/** 잘라내는 조상(overflow가 visible이 아닌 축이 하나라도 있는 element). 열린 동안 한 번만 수집한다. */
export function collectClipAncestors(element: Element): ClipAncestor[] {
  const ancestors: ClipAncestor[] = [];
  const view = element.ownerDocument.defaultView;
  if (!view) return ancestors;
  let current = element.parentElement;
  while (current && current !== element.ownerDocument.body) {
    const style = view.getComputedStyle(current);
    // 브라우저는 축별 값을 항상 준다. `|| overflow`는 축별 값을 비워 두는 happy-dom용 fallback.
    const x = CLIPPING_OVERFLOW.has(style.overflowX || style.overflow);
    const y = CLIPPING_OVERFLOW.has(style.overflowY || style.overflow);
    if (x || y) ancestors.push({ element: current, x, y });
    current = current.parentElement;
  }
  return ancestors;
}

export function isAnchorElementHidden(
  element: HTMLElement,
  clipAncestors: readonly ClipAncestor[],
  viewport: { width: number; height: number },
): boolean {
  if (!element.isConnected) return true;
  const rect = element.getBoundingClientRect();
  if (isRectOutside(rect, { top: 0, left: 0, right: viewport.width, bottom: viewport.height })) {
    return true;
  }
  return clipAncestors.some((ancestor) =>
    isRectOutside(rect, ancestor.element.getBoundingClientRect(), ancestor),
  );
}
